import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import SmartImage from 'dpl/components/SmartImage';
import { isGreaterThanBreakpoint } from 'dpl/util/grid';
import { BUYER_APP_STORE_URL } from 'dpl/shared/constants/urls';
import { isMobileSafari, isMobileUA, makeSequence } from 'dpl/shared/utils';
import { GDLinkV1 } from 'dpl/common/core';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { ANALYTICS_EVENTS } from 'dpl/constants/analytics';
import Icon from 'dpl/common/components/Icon';

import bannerDesktopPng from 'dpl/assets/reviews/app_banner-desktop.png';
import bannerMobileJpg from 'dpl/assets/reviews/app_banner-mobile.jpg';
import appStoreAppleSvg from 'dpl/assets/reviews/app_store-apple.svg';
import appStoreAndroidPng from 'dpl/assets/reviews/app_store-android.png';

const IS_MOBILE_UA = isMobileUA();

function trackAppStoreButtonClicked() {
  sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
    button_cta: 'App store images',
    view: 'reviews_app_download_banner'
  });
}

function AppStoreLink() {
  return (
    <GDLinkV1 onClick={trackAppStoreButtonClicked} href={BUYER_APP_STORE_URL}>
      {isMobileSafari() ? (
        <img src={appStoreAppleSvg} alt="Apple App Store badge" />
      ) : (
        <img
          src={appStoreAndroidPng}
          alt="Android Play Store badge"
          width={160}
        />
      )}
    </GDLinkV1>
  );
}

const TOTAL_STAR_COUNT = 5;

export default function AppReviewsBanner() {
  const {
    appleStoreReviewsCount,
    appleStoreReviewsScore,
    googleRatingsCount,
    googleScore
  } = useSelector(({ server }) => server);

  return (
    <div className="AppReviewsBanner bg-sand">
      <div className="container-xxl">
        <div className="row ml0 mr0 items-end">
          <div className="col-lg-6 f0 pr17-lg">
            <SmartImage
              src={
                isGreaterThanBreakpoint('sm')
                  ? bannerDesktopPng
                  : bannerMobileJpg
              }
              alt="Download the Good Dog app"
              className="w-100"
            />
          </div>
          <div className="col-lg-6">
            <div className="pv20-xl pv10-md pv6">
              <h4 className="lg:font-32 font-24 lh-title mb6 mb12-lg">
                {IS_MOBILE_UA
                  ? 'Thousands of trusted breeders. One app.'
                  : 'The #1 trusted app for finding your new best friend'}
              </h4>
              <div className="row mb8 mb12-lg">
                <div className="col-lg-6 col-12 mb6 mb0-lg">
                  <h5 className="font-20 lg:font-24 bb b--default pb2 mb4 mb5-lg lh-normal">
                    iOS
                  </h5>
                  <p className="font-16 fw-medium mb4">
                    {appleStoreReviewsScore} &bull; {appleStoreReviewsCount}{' '}
                    Ratings
                  </p>
                  <div className="inline-flex">
                    {makeSequence(TOTAL_STAR_COUNT).map(ratingIndex => (
                      <Icon
                        name="fetch-star-full"
                        height="24px"
                        width="24px"
                        key={ratingIndex}
                        className={classnames('tertiary', {
                          mr2: ratingIndex < TOTAL_STAR_COUNT - 1
                        })}
                      />
                    ))}
                  </div>
                </div>
                <div className="col-lg-6 col-12">
                  <h5 className="font-20 lg:font-24 bb b--default pb2 mb4 mb5-lg lh-normal">
                    Android
                  </h5>
                  <p className="font-16 fw-medium mb4">
                    {googleScore} &bull; {googleRatingsCount} Ratings
                  </p>
                  <div className="inline-flex">
                    {makeSequence(TOTAL_STAR_COUNT).map(ratingIndex => (
                      <Icon
                        name="fetch-star-full"
                        height="24px"
                        width="24px"
                        key={ratingIndex}
                        className={classnames('tertiary', {
                          mr2: ratingIndex < TOTAL_STAR_COUNT - 1
                        })}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {IS_MOBILE_UA ? (
                <AppStoreLink />
              ) : (
                <>
                  <p className="font-16 mb4">
                    Scan the QR code to get the app now
                  </p>
                  <QRCodeSVG value={BUYER_APP_STORE_URL} size={100} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
